import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { locationService } from "../services/locationService";
import { favoriteService } from "../services/favoriteService";
import { prewarmApi } from "../services/apiClient";

const STORAGE_TOKEN = "authToken";
const STORAGE_USER = "userInfo";
const STORAGE_FAVORITES = "favorite_locations";

function readStoredAuth() {
  try {
    const token = localStorage.getItem(STORAGE_TOKEN);
    const raw = localStorage.getItem(STORAGE_USER);
    const user = raw ? JSON.parse(raw) : null;
    const userId = user?.id || user?._id || null;

    return {
      token: token || null,
      userId: userId ? String(userId) : null,
    };
  } catch {
    return { token: null, userId: null };
  }
}

function normalizeId(value) {
  if (value == null) return "";

  if (typeof value === "string" || typeof value === "number") {
    return String(value).trim();
  }

  if (typeof value === "object") {
    return String(
      value._id ||
        value.id ||
        value.locationId ||
        value.locationMongoId ||
        value.location?._id ||
        value.location?.id ||
        ""
    ).trim();
  }

  return "";
}

function normalizeFavoriteList(list) {
  return Array.from(
    new Set(
      (Array.isArray(list) ? list : [])
        .map((item) => normalizeId(item))
        .filter(Boolean)
    )
  );
}

const AppContext = createContext(undefined);

const slugify = (text = "") => {
  return text
    .toString()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/đ/g, "d")
    .replace(/Đ/g, "D")
    .replace(/[^a-z0-9\s-]/g, "")
    .trim()
    .replace(/\s+/g, "-");
};

const getCategorySlug = (category) => {
  if (category === "culture") return "du-lich-van-hoa";
  if (category === "food") return "am-thuc-local";
  if (category === "stay") return "homestay-khach-san";
  return "du-lich-van-hoa";
};

const getCategoryLabel = (category) => {
  if (category === "culture") return "Du lịch văn hoá";
  if (category === "food") return "Ăn uống";
  if (category === "stay") return "Homestay - Khách sạn";
  return "Du lịch văn hoá";
};

const resolveCategorySlug = (item) => {
  const direct = item?.categorySlug && String(item.categorySlug).trim();
  if (direct) return direct.toLowerCase();

  const ref = item?.category;

  if (ref && typeof ref === "object" && ref.slug) {
    return String(ref.slug).trim().toLowerCase();
  }

  if (typeof ref === "string") {
    if (/^[a-f\d]{24}$/i.test(ref)) return "du-lich-van-hoa";
    return getCategorySlug(ref);
  }

  return "du-lich-van-hoa";
};

const SLUG_TO_LEGACY = {
  "du-lich-van-hoa": "culture",
  "am-thuc-local": "food",
  "homestay-khach-san": "stay",
};

const slugToLegacyCategory = (slug) =>
  SLUG_TO_LEGACY[String(slug || "").toLowerCase()] || "culture";

// Admin lưu "loại hình" (subtype) vào `categorySlug`, nên không thể suy ra danh mục lớn
// chỉ từ trường đó. Keep in sync with SUBTYPE_GROUPS in admin/LocationEditorModal.js.
const SUBTYPE_TO_MAIN = {
  "du-lich": "du-lich-van-hoa",
  chua: "du-lich-van-hoa",
  "nha-tho": "du-lich-van-hoa",
  "di-tich": "du-lich-van-hoa",
  "ca-phe-local": "am-thuc-local",
  "ca-phe-co-view": "am-thuc-local",
  "quan-lau-nuong": "am-thuc-local",
  "quan-com-gia-dinh": "am-thuc-local",
  "quan-an-no": "am-thuc-local",
  "quan-an-vat": "am-thuc-local",
  "home-co-view": "homestay-khach-san",
  "home-tien-nghi": "homestay-khach-san",
  "khach-san": "homestay-khach-san",
};

// Same aliases as canonicalMainCategorySlug in admin/LocationEditorModal.js.
const canonicalMainSlug = (raw) => {
  const s = slugify(raw || "");
  if (!s) return "";
  if (["du-lich-van-hoa", "diem-van-hoa", "diem-van-hoa-local"].includes(s)) return "du-lich-van-hoa";
  if (["am-thuc-local", "am-thuc-dia-phuong", "am-thuc", "an-uong-local", "an-uong"].includes(s)) return "am-thuc-local";
  if (["homestay-khach-san", "homestay"].includes(s)) return "homestay-khach-san";
  return "";
};

/** Danh mục lớn → culture | food | stay. Ưu tiên danh mục thật (Category) admin đã chọn. */
const resolveLegacyCategory = (item, categorySlugResolved) => {
  const ref = item?.category;
  if (typeof ref === "string" && ["culture", "food", "stay"].includes(ref)) return ref;

  const main =
    (ref && typeof ref === "object" && canonicalMainSlug(ref.slug || ref.name)) ||
    canonicalMainSlug(item?.categoryName) ||
    canonicalMainSlug(categorySlugResolved) ||
    SUBTYPE_TO_MAIN[categorySlugResolved] ||
    "";
  return slugToLegacyCategory(main);
};

const labelFromSlugFallback = (slug) => {
  switch (String(slug || "").toLowerCase()) {
    case "du-lich-van-hoa":
      return "Du lịch văn hoá";
    case "am-thuc-local":
      return "Ăn uống";
    case "homestay-khach-san":
      return "Homestay & Lưu trú";
    default:
      return "";
  }
};

const pickTrim = (v) =>
  v != null && String(v).trim() ? String(v).trim() : "";

function plainTextSnippet(htmlOrText, maxLen = 280) {
  if (htmlOrText == null) return "";

  const t = String(htmlOrText)
    .replace(/<[^>]+>/g, " ")
    .replace(/\s+/g, " ")
    .trim();

  if (!t) return "";
  if (t.length <= maxLen) return t;

  return `${t.slice(0, maxLen).trim()}…`;
}

function resolveShortIntro(item) {
  return (
    pickTrim(item.shortIntro) ||
    pickTrim(item.summary) ||
    pickTrim(item.intro) ||
    pickTrim(item.seoDescription) ||
    plainTextSnippet(item.description) ||
    ""
  );
}

export const AppProvider = ({ children }) => {
  const [locations, setLocations] = useState([]);
  const [filter, setFilter] = useState({ category: "all" });
  const [favorites, setFavorites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    prewarmApi();
  }, []);

  useEffect(() => {
    try {
      const saved = localStorage.getItem(STORAGE_FAVORITES);

      if (saved) {
        const parsed = JSON.parse(saved);
        setFavorites(normalizeFavoriteList(parsed));
      }
    } catch {
      setFavorites([]);
    }
  }, []);

  useEffect(() => {
    localStorage.setItem(STORAGE_FAVORITES, JSON.stringify(favorites));
  }, [favorites]);

  useEffect(() => {
    const syncFavoritesFromDb = async () => {
      const { token, userId } = readStoredAuth();

      if (!token || !userId) {
        try {
          const saved = localStorage.getItem(STORAGE_FAVORITES);

          if (saved) {
            const parsed = JSON.parse(saved);
            setFavorites(normalizeFavoriteList(parsed));
          }
        } catch {
          // keep current
        }

        return;
      }

      try {
        const data = await favoriteService.getUserFavorites(token);
        setFavorites(normalizeFavoriteList(data));
      } catch {
        // giữ localStorage nếu API lỗi
      }
    };

    syncFavoritesFromDb();

    window.addEventListener("dalat-auth-changed", syncFavoritesFromDb);

    return () => {
      window.removeEventListener("dalat-auth-changed", syncFavoritesFromDb);
    };
  }, []);

  useEffect(() => {
    const fetchLocations = async () => {
      try {
        setLoading(true);
        setError("");

        const raw = await locationService.getLocations();

        const data = Array.isArray(raw)
          ? raw
          : Array.isArray(raw?.data)
            ? raw.data
            : [];

        const normalizedData =
          data.length > 0
            ? data.map((item, index) => {
                const name = item.name || item.title || "Chưa có tên";
                const categorySlugResolved = resolveCategorySlug(item);
                const refCat = item.category;

                const legacyKey = resolveLegacyCategory(item, categorySlugResolved);

                const lat =
                  item.latitude ?? item.lat ?? item.coords?.[0] ?? 11.9404;
                const lng =
                  item.longitude ?? item.lng ?? item.coords?.[1] ?? 108.4583;

                const rawId = item._id || item.id || index + 1;
                const safeId = String(rawId);

                const categoryDisplayName =
                  (typeof refCat === "object" &&
                    refCat?.name &&
                    String(refCat.name).trim()) ||
                  (item.categoryName && String(item.categoryName).trim()) ||
                  (item.categoryLabel && String(item.categoryLabel).trim()) ||
                  labelFromSlugFallback(categorySlugResolved) ||
                  getCategoryLabel(legacyKey);

                return {
                  id: safeId,
                  _id: item._id || item.id || null,
                  name,
                  title: item.title || name,
                  slug: item.slug || slugify(name),

                  category: legacyKey,
                  categorySlug: categorySlugResolved,
                  categoryLabel: item.categoryLabel || categoryDisplayName,
                  categoryName: item.categoryName || categoryDisplayName,

                  categoryId: item.category?._id || item.category || null,
                  viewCount: Number(item.viewCount || 0),
                  rating: Number(item.rating || 0),
                  ratingCount: Number(item.ratingCount || 0),
                  createdAt: item.createdAt || null,

                  address: pickTrim(item.address) || pickTrim(item.location) || "",
                  ward: pickTrim(item.ward) || "",
                  district: pickTrim(item.district) || "",
                  city: pickTrim(item.city) || "",
                  phone: item.phone || item.sdt || item.contact || "",
                  shortIntro: resolveShortIntro(item),
                  seoDescription: pickTrim(item.seoDescription) || "",
                  description:
                    item.description != null && String(item.description).trim()
                      ? String(item.description)
                      : pickTrim(item.shortIntro) || "",

                  image:
                    item.image ||
                    item.imageUrl ||
                    (Array.isArray(item.gallery) && item.gallery[0]) ||
                    "https://images.unsplash.com/photo-1500530855697-b586d89ba3ee?auto=format&fit=crop&w=1200&q=80",

                  gallery: Array.isArray(item.gallery)
                    ? item.gallery
                    : item.images && Array.isArray(item.images)
                      ? item.images
                      : item.image
                        ? [item.image, item.image]
                        : [],

                  tiktokEmbed: item.tiktokEmbed || "",
                  tiktokUrl: item.tiktokUrl || item.videoUrl || "",

                  coords: item.coords || [lat, lng],
                  lat,
                  lng,
                  latitude: lat,
                  longitude: lng,
                  mapIcon:
                    item.mapIcon != null && item.mapIcon !== ""
                      ? String(item.mapIcon)
                      : "all",
                };
              })
            : [];

        setLocations(normalizedData);

        if (normalizedData.length === 0 && data.length > 0) {
          console.warn(
            "[AppContext] locations parsed as empty — check API response format"
          );
        }
      } catch (err) {
        setError(err.message || "Đã có lỗi xảy ra");
      } finally {
        setLoading(false);
      }
    };

    fetchLocations();
  }, []);

  const toggleFavorite = useCallback(async (id) => {
    const locationId = normalizeId(id);

    if (!locationId) return;

    const { token, userId } = readStoredAuth();

    const applyToggle = () => {
      setFavorites((prev) => {
        const clean = normalizeFavoriteList(prev);

        return clean.includes(locationId)
          ? clean.filter((item) => item !== locationId)
          : [...clean, locationId];
      });
    };

    if (!token || !userId) {
      applyToggle();
      return;
    }

    applyToggle();

    try {
      const res = await favoriteService.toggleFavorite(token, locationId);

      const nextRaw =
        res?.isFavorited ??
        res?.favorited ??
        res?.favorite ??
        res?.data?.isFavorited ??
        res?.data?.favorited ??
        res?.data?.favorite;

      const next =
        typeof nextRaw === "boolean"
          ? nextRaw
          : nextRaw === "true"
            ? true
            : nextRaw === "false"
              ? false
              : null;

      if (next === null) return;

      setFavorites((prev) => {
        const clean = normalizeFavoriteList(prev);

        return next
          ? Array.from(new Set([...clean, locationId]))
          : clean.filter((item) => item !== locationId);
      });
    } catch (e) {
      applyToggle();
      console.error("[favorites] toggle failed:", e);
    }
  }, []);

  const isFavorite = useCallback(
    (id) => {
      const locationId = normalizeId(id);

      if (!locationId) return false;

      return normalizeFavoriteList(favorites).includes(locationId);
    },
    [favorites]
  );

  const filteredLocations = useMemo(() => {
    if (filter.category === "all") return locations;

    return locations.filter(
      (item) =>
        item.categorySlug === filter.category || item.category === filter.category
    );
  }, [locations, filter]);

  const value = useMemo(
    () => ({
      locations,
      setLocations,
      filteredLocations,
      filter,
      setFilter,
      favorites,
      setFavorites,
      toggleFavorite,
      isFavorite,
      loading,
      error,
    }),
    [
      locations,
      filteredLocations,
      filter,
      favorites,
      toggleFavorite,
      isFavorite,
      loading,
      error,
    ]
  );

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
};

export const useAppContext = () => {
  const context = useContext(AppContext);

  if (!context) {
    throw new Error("useAppContext must be used within AppProvider");
  }

  return context;
};